import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { MessageCircle, Send, Bot, User, Loader, X, Sparkles, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

// ============================================
// AI CONTEXT CONFIGURATION PER PAGE
// ============================================

export const PAGE_CONFIGS = {
  // Home Dashboard
  'home': {
    name: 'Home Dashboard',
    systemPrompt: `You are InvestorIQ AI, a financial educator helping users navigate the home dashboard. 
    Focus on: market overview, getting started tips, feature explanations.
    Always remind users this is for educational purposes only.`,
    quickPrompts: [
      "What features should I explore first?",
      "How's the market doing today?",
      "Explain the dashboard metrics",
      "What should a beginner investor focus on?",
      "How do I start building a portfolio?"
    ],
    contextBuilder: (data) => ({
      page: 'Home Dashboard',
      marketSummary: data?.marketSummary,
      userPortfolio: data?.portfolio
    })
  },

  // Stock/Company Analysis
  'company': {
    name: 'Company Analysis',
    systemPrompt: `You are InvestorIQ AI, analyzing company fundamentals and financials.
    You have access to: stock data, financials, earnings, health scores.
    Explain metrics in simple terms. Compare to industry when relevant.
    Always remind users to do their own research before investing.`,
    quickPrompts: [
      "Is this stock overvalued or undervalued?",
      "Explain the P/E ratio for this company",
      "What do the financials tell us?",
      "Is this a good long-term investment?",
      "What are the key risks?"
    ],
    contextBuilder: (data) => ({
      page: 'Company Analysis',
      stock: data?.stock,
      tab: data?.activeTab,
      financials: data?.financials,
      earnings: data?.earnings,
      healthScore: data?.healthScore
    })
  },

  // Portfolio Management
  'portfolio': {
    name: 'Portfolio',
    systemPrompt: `You are InvestorIQ AI, helping users understand their portfolio.
    Focus on: diversification, risk management, performance analysis.
    Provide educational insights about portfolio construction.
    Never give specific buy/sell recommendations.`,
    quickPrompts: [
      "Analyze my portfolio diversification",
      "What's my portfolio's risk level?",
      "Am I too concentrated in one sector?",
      "How can I reduce portfolio volatility?",
      "Explain my portfolio performance"
    ],
    contextBuilder: (data) => ({
      page: 'Portfolio',
      holdings: data?.holdings,
      totalValue: data?.totalValue,
      performance: data?.performance,
      allocation: data?.allocation
    })
  },

  // Portfolio Rebalancer
  'rebalancer': {
    name: 'Portfolio Rebalancer',
    systemPrompt: `You are InvestorIQ AI, helping users understand portfolio rebalancing.
    Explain: target allocations, drift, rebalancing strategies.
    Educational focus on why rebalancing matters.
    This is educational - consult a financial advisor for personal advice.`,
    quickPrompts: [
      "Why is my portfolio drifting?",
      "How often should I rebalance?",
      "Explain target allocation strategies",
      "What's a good allocation for my age?",
      "Should I rebalance now?"
    ],
    contextBuilder: (data) => ({
      page: 'Portfolio Rebalancer',
      currentAllocation: data?.currentAllocation,
      targetAllocation: data?.targetAllocation,
      drift: data?.drift,
      recommendations: data?.recommendations
    })
  },

  // Market Anomaly Detector
  'anomaly-detector': {
    name: 'Market Anomaly Detector',
    systemPrompt: `You are InvestorIQ AI, explaining market anomalies and unusual stock behavior.
    Focus on: RSI extremes, unusual volume, price gaps, technical analysis.
    Explain what anomalies mean and their potential implications.
    Educational only - anomalies don't guarantee future movements.`,
    quickPrompts: [
      "What does RSI overbought mean?",
      "Why is unusual volume important?",
      "Explain price gaps",
      "Should I buy oversold stocks?",
      "How reliable are these signals?"
    ],
    contextBuilder: (data) => ({
      page: 'Market Anomaly Detector',
      anomalies: data?.anomalies,
      summary: data?.summary,
      selectedStock: data?.selectedStock
    })
  },

  // Forex Dashboard
  'forex': {
    name: 'Forex Dashboard',
    systemPrompt: `You are InvestorIQ AI, explaining currency exchange and forex markets.
    Focus on: currency pairs, exchange rates, forex basics.
    Explain factors affecting currency movements.
    Forex trading is risky - this is educational content only.`,
    quickPrompts: [
      "What affects USD/EUR exchange rate?",
      "Explain currency pair notation",
      "What is forex spread?",
      "How do interest rates affect currencies?",
      "Is forex trading risky?"
    ],
    contextBuilder: (data) => ({
      page: 'Forex Dashboard',
      pairs: data?.pairs,
      selectedPair: data?.selectedPair,
      rates: data?.rates
    })
  },

  // Options Strategy Builder
  'options': {
    name: 'Options Strategy',
    systemPrompt: `You are InvestorIQ AI, explaining options trading concepts.
    Focus on: options basics, strategies, Greeks, risk management.
    Options are complex and risky - emphasize education.
    Never recommend specific trades. Always suggest paper trading first.`,
    quickPrompts: [
      "Explain calls vs puts",
      "What is implied volatility?",
      "How do the Greeks work?",
      "What's a covered call strategy?",
      "Why do options expire worthless?"
    ],
    contextBuilder: (data) => ({
      page: 'Options Strategy',
      strategy: data?.strategy,
      legs: data?.legs,
      greeks: data?.greeks,
      profitLoss: data?.profitLoss
    })
  },

  // ETF Analysis
  'etf': {
    name: 'ETF Analysis',
    systemPrompt: `You are InvestorIQ AI, helping users understand ETFs.
    Focus on: expense ratios, holdings, diversification, ETF comparison.
    Explain ETF benefits and considerations.
    Educational content - not specific recommendations.`,
    quickPrompts: [
      "What's a good expense ratio?",
      "How do ETFs differ from mutual funds?",
      "Explain ETF holdings overlap",
      "Are index ETFs better than active?",
      "How do I choose between similar ETFs?"
    ],
    contextBuilder: (data) => ({
      page: 'ETF Analysis',
      etf: data?.etf,
      holdings: data?.holdings,
      comparison: data?.comparison
    })
  },

  // Crypto Analysis
  'crypto': {
    name: 'Crypto Analysis',
    systemPrompt: `You are InvestorIQ AI, explaining cryptocurrency markets.
    Focus on: crypto basics, blockchain, market dynamics, risks.
    Crypto is highly volatile - emphasize risk awareness.
    Educational only - never give trading advice.`,
    quickPrompts: [
      "What drives Bitcoin's price?",
      "Explain cryptocurrency correlation",
      "What is DeFi?",
      "How much crypto should I hold?",
      "What are the risks of crypto?"
    ],
    contextBuilder: (data) => ({
      page: 'Crypto Analysis',
      coins: data?.coins,
      correlation: data?.correlation,
      marketCap: data?.marketCap
    })
  },

  // Education Center
  'education': {
    name: 'Education Center',
    systemPrompt: `You are InvestorIQ AI, a comprehensive financial educator.
    Answer questions about any investing topic in simple terms.
    Use examples and analogies. Be patient and thorough.
    Encourage users to continue learning.`,
    quickPrompts: [
      "What should a beginner learn first?",
      "Explain compound interest",
      "What's the difference between stocks and bonds?",
      "How do I read a stock chart?",
      "What is dollar-cost averaging?"
    ],
    contextBuilder: (data) => ({
      page: 'Education Center',
      currentTopic: data?.topic,
      progress: data?.progress
    })
  },

  // Watchlist
  'watchlist': {
    name: 'Watchlist',
    systemPrompt: `You are InvestorIQ AI, helping users manage their watchlist.
    Focus on: stock screening, watchlist organization, price alerts.
    Help users understand what to watch for in stocks.
    Educational guidance only.`,
    quickPrompts: [
      "What stocks should I watch?",
      "How do I organize my watchlist?",
      "When should I set price alerts?",
      "How many stocks should I track?",
      "What metrics matter most?"
    ],
    contextBuilder: (data) => ({
      page: 'Watchlist',
      watchlist: data?.watchlist,
      alerts: data?.alerts
    })
  },

  // Earnings Calendar
  'earnings': {
    name: 'Earnings Calendar',
    systemPrompt: `You are InvestorIQ AI, explaining earnings reports and their impact.
    Focus on: earnings dates, expectations, reactions, strategies.
    Explain how earnings affect stock prices.
    Educational - not trading recommendations.`,
    quickPrompts: [
      "How do earnings affect stock price?",
      "What is an earnings beat?",
      "Should I hold through earnings?",
      "Explain earnings whisper numbers",
      "Why do stocks drop on good earnings?"
    ],
    contextBuilder: (data) => ({
      page: 'Earnings Calendar',
      upcomingEarnings: data?.upcoming,
      selectedCompany: data?.selected
    })
  },

  // DCA Calculator
  'dca': {
    name: 'DCA Calculator',
    systemPrompt: `You are InvestorIQ AI, explaining dollar-cost averaging strategy.
    Focus on: DCA benefits, implementation, long-term investing.
    Explain how regular investing reduces timing risk.
    Educational content about systematic investing.`,
    quickPrompts: [
      "What are the benefits of DCA?",
      "How much should I invest monthly?",
      "DCA vs lump sum - which is better?",
      "How long should I DCA?",
      "Does DCA work in a bear market?"
    ],
    contextBuilder: (data) => ({
      page: 'DCA Calculator',
      investment: data?.investment,
      frequency: data?.frequency,
      projections: data?.projections
    })
  },

  // Congress Trades
  'congress': {
    name: 'Congress Trades',
    systemPrompt: `You are InvestorIQ AI, explaining congressional trading disclosures.
    Focus on: disclosure rules, tracking trades, implications.
    Explain what congressional trades might indicate.
    This is public information - not investment advice.`,
    quickPrompts: [
      "Why track congress trades?",
      "How delayed are these disclosures?",
      "Do congress members outperform?",
      "What are the disclosure rules?",
      "Should I copy these trades?"
    ],
    contextBuilder: (data) => ({
      page: 'Congress Trades',
      recentTrades: data?.trades,
      topTraders: data?.topTraders
    })
  },

  // Recession Dashboard
  'recession': {
    name: 'Recession Indicators',
    systemPrompt: `You are InvestorIQ AI, explaining economic indicators and recession signals.
    Focus on: yield curve, unemployment, GDP, leading indicators.
    Explain what each indicator means historically.
    Economic forecasting is uncertain - provide context not predictions.`,
    quickPrompts: [
      "What is the yield curve telling us?",
      "How reliable are recession indicators?",
      "What should I do in a recession?",
      "Explain the leading indicators",
      "How long do recessions last?"
    ],
    contextBuilder: (data) => ({
      page: 'Recession Indicators',
      indicators: data?.indicators,
      probability: data?.probability
    })
  },

  // Portfolio Analytics
  'portfolio-analytics': {
    name: 'Portfolio Analytics',
    systemPrompt: `You are InvestorIQ AI, explaining advanced portfolio analytics.
    Focus on: Sharpe ratio, beta, correlation, risk metrics.
    Explain concepts simply. This is educational content only.`,
    quickPrompts: [
      "What is Sharpe ratio?",
      "Explain my portfolio beta",
      "How correlated are my holdings?",
      "What does diversification score mean?",
      "How can I improve risk-adjusted returns?"
    ],
    contextBuilder: (data) => ({ page: 'Portfolio Analytics', ...data })
  },

  // Risk Analyzer
  'risk-analyzer': {
    name: 'Risk Analyzer',
    systemPrompt: `You are InvestorIQ AI, explaining portfolio risk analysis.
    Focus on: volatility, VaR, drawdown, stress testing.
    Help users understand risk without causing alarm.`,
    quickPrompts: [
      "What is Value at Risk (VaR)?",
      "Explain my risk score",
      "What is maximum drawdown?",
      "How volatile is my portfolio?",
      "What does stress test show?"
    ],
    contextBuilder: (data) => ({ page: 'Risk Analyzer', ...data })
  },

  // Price Alerts
  'alerts': {
    name: 'Price Alerts',
    systemPrompt: `You are InvestorIQ AI, helping users set up price alerts.
    Focus on: alert types, trigger conditions, notification strategies.
    Educational guidance on when alerts are useful.`,
    quickPrompts: [
      "What types of alerts should I set?",
      "How do I use RSI alerts?",
      "When should I set price alerts?",
      "What's a good alert strategy?",
      "How many alerts should I have?"
    ],
    contextBuilder: (data) => ({ page: 'Price Alerts', ...data })
  },

  // Options Chain
  'options-chain': {
    name: 'Options Chain',
    systemPrompt: `You are InvestorIQ AI, explaining options chains and pricing.
    Focus on: strike selection, expiration, Greeks, IV.
    Options are complex - emphasize education and paper trading.`,
    quickPrompts: [
      "How do I read an options chain?",
      "What strike should I choose?",
      "Explain implied volatility",
      "What does open interest mean?",
      "How do I pick expiration dates?"
    ],
    contextBuilder: (data) => ({ page: 'Options Chain', ...data })
  },

  // Options Screener
  'options-screener': {
    name: 'Options Screener',
    systemPrompt: `You are InvestorIQ AI, helping users screen for options opportunities.
    Focus on: filtering criteria, unusual activity, risk assessment.
    Educational only - not trading recommendations.`,
    quickPrompts: [
      "What makes a good options screen?",
      "How do I find high IV options?",
      "What is unusual options activity?",
      "How do I filter by Greeks?",
      "What screens do pros use?"
    ],
    contextBuilder: (data) => ({ page: 'Options Screener', ...data })
  },

  // IV Crush Predictor
  'iv-crush': {
    name: 'IV Crush Predictor',
    systemPrompt: `You are InvestorIQ AI, explaining implied volatility and IV crush.
    Focus on: earnings IV, expected moves, volatility trading.
    This is complex - be thorough but clear.`,
    quickPrompts: [
      "What is IV crush?",
      "How does earnings affect IV?",
      "What is IV rank vs IV percentile?",
      "How do I trade around earnings?",
      "Why does IV drop after earnings?"
    ],
    contextBuilder: (data) => ({ page: 'IV Crush Predictor', ...data })
  },

  // ETF X-Ray
  'etf-xray': {
    name: 'ETF X-Ray',
    systemPrompt: `You are InvestorIQ AI, analyzing ETF holdings and structure.
    Focus on: holdings breakdown, expense ratios, sector exposure.
    Help users understand what's inside their ETFs.`,
    quickPrompts: [
      "What are the top holdings?",
      "Is this expense ratio good?",
      "How diversified is this ETF?",
      "What sectors am I exposed to?",
      "How does this compare to similar ETFs?"
    ],
    contextBuilder: (data) => ({ page: 'ETF X-Ray', ...data })
  },

  // ETF Comparison
  'etf-compare': {
    name: 'ETF Comparison',
    systemPrompt: `You are InvestorIQ AI, comparing ETFs side by side.
    Focus on: fees, performance, holdings overlap, tracking error.
    Help users choose between similar ETFs.`,
    quickPrompts: [
      "Which ETF has lower fees?",
      "How much do these overlap?",
      "Which performed better?",
      "What's the tracking difference?",
      "Which should I choose?"
    ],
    contextBuilder: (data) => ({ page: 'ETF Comparison', ...data })
  },

  // Crypto Correlation
  'crypto-correlation': {
    name: 'Crypto Correlation',
    systemPrompt: `You are InvestorIQ AI, explaining cryptocurrency correlations.
    Focus on: correlation meaning, diversification, market dynamics.
    Crypto is volatile - emphasize risk awareness.`,
    quickPrompts: [
      "Why are cryptos correlated?",
      "How do I diversify in crypto?",
      "What drives correlation changes?",
      "Is Bitcoin correlated to stocks?",
      "Which cryptos are least correlated?"
    ],
    contextBuilder: (data) => ({ page: 'Crypto Correlation', ...data })
  },

  // Insider Trading
  'insider': {
    name: 'Insider Trading',
    systemPrompt: `You are InvestorIQ AI, explaining SEC insider trading filings.
    Focus on: Form 4 filings, insider signals, interpretation.
    This is public data - educational analysis only.`,
    quickPrompts: [
      "What do insider buys signal?",
      "Why do insiders sell?",
      "What is a cluster buy?",
      "How reliable are insider signals?",
      "How do I track insiders?"
    ],
    contextBuilder: (data) => ({ page: 'Insider Trading', ...data })
  },

  // Whale Watcher
  'whale': {
    name: 'Whale Watcher',
    systemPrompt: `You are InvestorIQ AI, explaining large institutional activity.
    Focus on: block trades, dark pools, institutional flow.
    Help users understand market structure.`,
    quickPrompts: [
      "What is a whale trade?",
      "How do dark pools work?",
      "What does unusual volume mean?",
      "How do I follow institutions?",
      "Are whale trades reliable signals?"
    ],
    contextBuilder: (data) => ({ page: 'Whale Watcher', ...data })
  },

  // Market Dashboard
  'market': {
    name: 'Market Dashboard',
    systemPrompt: `You are InvestorIQ AI, providing market overview analysis.
    Focus on: indices, breadth, VIX, sector rotation.
    Give balanced market perspective.`,
    quickPrompts: [
      "How's the market today?",
      "What is market breadth?",
      "What does VIX tell us?",
      "Which sectors are leading?",
      "Is this a bull or bear market?"
    ],
    contextBuilder: (data) => ({ page: 'Market Dashboard', ...data })
  },

  // Sector Analysis
  'sector': {
    name: 'Sector Analysis',
    systemPrompt: `You are InvestorIQ AI, explaining sector performance and rotation.
    Focus on: sector cycles, relative strength, allocation.
    Educational content about sector investing.`,
    quickPrompts: [
      "Which sector is strongest?",
      "What is sector rotation?",
      "How do I allocate by sector?",
      "Why do sectors move together?",
      "What sector for this market?"
    ],
    contextBuilder: (data) => ({ page: 'Sector Analysis', ...data })
  },

  // Social Sentiment
  'sentiment': {
    name: 'Social Sentiment',
    systemPrompt: `You are InvestorIQ AI, explaining social media sentiment analysis.
    Focus on: sentiment indicators, social volume, crowd psychology.
    Sentiment can be misleading - emphasize critical thinking.`,
    quickPrompts: [
      "What does sentiment score mean?",
      "Is high social volume bullish?",
      "How reliable is sentiment?",
      "When is sentiment wrong?",
      "How do I use sentiment data?"
    ],
    contextBuilder: (data) => ({ page: 'Social Sentiment', ...data })
  },

  // Technical Indicators
  'technical': {
    name: 'Technical Indicators',
    systemPrompt: `You are InvestorIQ AI, explaining technical analysis indicators.
    Focus on: RSI, MACD, moving averages, patterns.
    Technical analysis has limitations - be balanced.`,
    quickPrompts: [
      "What does RSI mean?",
      "How do I read MACD?",
      "Explain moving averages",
      "What are Bollinger Bands?",
      "Is technical analysis reliable?"
    ],
    contextBuilder: (data) => ({ page: 'Technical Indicators', ...data })
  },

  // Stock Comparison
  'compare': {
    name: 'Stock Comparison',
    systemPrompt: `You are InvestorIQ AI, comparing stocks side by side.
    Focus on: valuation, growth, profitability metrics.
    Help users evaluate relative value.`,
    quickPrompts: [
      "Which stock is cheaper?",
      "Compare the growth rates",
      "Which is more profitable?",
      "What about dividends?",
      "Which is the better value?"
    ],
    contextBuilder: (data) => ({ page: 'Stock Comparison', ...data })
  },

  // Stock News
  'news': {
    name: 'Stock News',
    systemPrompt: `You are InvestorIQ AI, helping users understand stock news.
    Focus on: news impact, sentiment, material events.
    Help distinguish noise from signal.`,
    quickPrompts: [
      "Is this news material?",
      "How will this affect the stock?",
      "What's the sentiment?",
      "Should I react to this news?",
      "Is this priced in?"
    ],
    contextBuilder: (data) => ({ page: 'Stock News', ...data })
  },

  // Alpha Intelligence
  'alpha': {
    name: 'Alpha Intelligence',
    systemPrompt: `You are InvestorIQ AI, explaining alpha generation signals.
    Focus on: signal interpretation, confidence levels, risk/reward.
    These are educational signals - not recommendations.`,
    quickPrompts: [
      "What is alpha?",
      "How confident is this signal?",
      "What's the risk/reward?",
      "How are signals generated?",
      "Should I act on this?"
    ],
    contextBuilder: (data) => ({ page: 'Alpha Intelligence', ...data })
  },

  // Default fallback
  'default': {
    name: 'InvestorIQ',
    systemPrompt: `You are InvestorIQ AI, a comprehensive financial educator.
    Help users understand investing concepts, market dynamics, and financial planning.
    Be educational, clear, and always remind users to do their own research.
    Never give specific investment recommendations.`,
    quickPrompts: [
      "How do I get started investing?",
      "What's the market doing today?",
      "Explain portfolio diversification",
      "How do I analyze a stock?",
      "What should beginners avoid?"
    ],
    contextBuilder: (data) => ({
      page: 'General',
      ...data
    })
  }
};

// ============================================
// AI CONTEXT PROVIDER
// ============================================

const AIContext = createContext(null);

export const AIProvider = ({ children }) => {
  const [currentPage, setCurrentPage] = useState('default');
  const [pageData, setPageData] = useState({});
  const [portfolio, setPortfolio] = useState(null);

  const value = {
    currentPage,
    setCurrentPage,
    pageData,
    setPageData,
    portfolio,
    setPortfolio,
    getConfig: () => PAGE_CONFIGS[currentPage] || PAGE_CONFIGS['default']
  };

  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  );
};

export const useAI = () => useContext(AIContext);

// ============================================
// PAGE-AWARE AI CHAT COMPONENT
// ============================================

const PageAwareAI = ({ 
  pageId,           // Which page we're on (e.g., 'company', 'portfolio', 'anomaly-detector')
  pageData = {},    // Page-specific data to send to AI
  portfolio,        // User's portfolio (optional)
  symbol,           // Current stock symbol (optional)
  onClose           // Optional close callback
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPrompts, setShowPrompts] = useState(true);
  const messagesEndRef = useRef(null);

  // Get page configuration
  const config = PAGE_CONFIGS[pageId] || PAGE_CONFIGS['default'];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reset messages when page changes
  useEffect(() => {
    setMessages([]);
    setShowPrompts(true);
  }, [pageId]);

  // Build context for AI
  const buildContext = () => {
    const baseContext = config.contextBuilder(pageData);
    
    return {
      ...baseContext,
      portfolio: portfolio ? {
        holdings: portfolio.holdings,
        totalValue: portfolio.totalValue,
        performance: portfolio.performance
      } : null,
      currentSymbol: symbol,
      timestamp: new Date().toISOString()
    };
  };

  // Build system message
  const buildSystemMessage = () => {
    return `${config.systemPrompt}

IMPORTANT GUIDELINES:
1. You are an AI financial EDUCATOR, not a financial advisor
2. Always remind users to consult a licensed financial advisor for personal advice
3. Never give specific buy/sell recommendations
4. Explain concepts in simple, clear language
5. Use examples when helpful
6. If you don't know something, say so
7. Always mention that investing involves risk

Current Page: ${config.name}
User Context: ${JSON.stringify(buildContext(), null, 2)}`;
  };

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setShowPrompts(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api'}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          context: buildContext(),
          systemPrompt: buildSystemMessage(),
          pageId: pageId
        })
      });

      const data = await response.json();
      
      const aiMessage = {
        role: 'assistant',
        content: data.message,
        usage: data.usage,
        demo: data.demo
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    setInput(prompt);
    sendMessage(prompt);
  };

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          border: 'none',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 10px 25px rgba(139, 92, 246, 0.5)',
          zIndex: 1000,
          transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 15px 35px rgba(139, 92, 246, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 10px 25px rgba(139, 92, 246, 0.5)';
        }}
        title={`AI Assistant - ${config.name}`}
      >
        <Sparkles size={28} color="white" />
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '2rem',
      right: '2rem',
      width: isMinimized ? '300px' : '400px',
      maxWidth: '90vw',
      height: isMinimized ? 'auto' : '600px',
      maxHeight: '80vh',
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      border: '1px solid #475569',
      borderRadius: '1rem',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      transition: 'all 0.3s ease'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
        padding: '0.75rem 1rem',
        borderRadius: isMinimized ? '1rem' : '1rem 1rem 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Sparkles size={20} color="white" />
          <div>
            <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 'bold' }}>
              InvestorIQ AI
            </h3>
            <p style={{ margin: 0, fontSize: '0.65rem', opacity: 0.9 }}>
              {config.name}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '0.375rem',
              padding: '0.375rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {isMinimized ? <ChevronUp size={16} color="white" /> : <ChevronDown size={16} color="white" />}
          </button>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '0.375rem',
              padding: '0.375rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={16} color="white" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages Area */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem'
          }}>
            {/* Welcome & Quick Prompts */}
            {messages.length === 0 && showPrompts && (
              <div style={{ textAlign: 'center', padding: '1rem 0.5rem' }}>
                <Bot size={40} color="#8b5cf6" style={{ margin: '0 auto 0.75rem' }} />
                <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
                  Hi! I'm your AI assistant for <strong>{config.name}</strong>. 
                  Ask me anything about this feature!
                </p>
                
                {/* Page-specific quick prompts */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {config.quickPrompts.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuickPrompt(prompt)}
                      style={{
                        padding: '0.625rem 0.75rem',
                        background: 'rgba(139, 92, 246, 0.1)',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: '0.5rem',
                        color: '#c4b5fd',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                      }}
                    >
                      <HelpCircle size={14} />
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Messages */}
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'flex-start',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: msg.role === 'user' ? '#3b82f6' : '#8b5cf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
                </div>
                <div style={{
                  flex: 1,
                  padding: '0.625rem 0.875rem',
                  background: msg.role === 'user' 
                    ? 'rgba(59, 130, 246, 0.2)' 
                    : msg.error 
                    ? 'rgba(239, 68, 68, 0.2)'
                    : 'rgba(51, 65, 85, 0.5)',
                  border: `1px solid ${msg.role === 'user' ? '#3b82f6' : msg.error ? '#ef4444' : '#475569'}`,
                  borderRadius: '0.75rem',
                  fontSize: '0.8rem',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap'
                }}>
                  {msg.content}
                  {msg.demo && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.375rem',
                      background: 'rgba(251, 191, 36, 0.1)',
                      border: '1px solid rgba(251, 191, 36, 0.3)',
                      borderRadius: '0.375rem',
                      fontSize: '0.65rem',
                      color: '#fbbf24'
                    }}>
                      ⚠️ Demo mode
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#8b5cf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Bot size={14} />
                </div>
                <div style={{
                  padding: '0.625rem 0.875rem',
                  background: 'rgba(51, 65, 85, 0.5)',
                  border: '1px solid #475569',
                  borderRadius: '0.75rem'
                }}>
                  <Loader size={14} className="spin" color="#8b5cf6" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={{
            padding: '0.75rem',
            borderTop: '1px solid #475569'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={`Ask about ${config.name.toLowerCase()}...`}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '0.625rem 0.75rem',
                  background: '#0f172a',
                  border: '1px solid #475569',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.8rem'
                }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                style={{
                  padding: '0.625rem 0.875rem',
                  background: loading || !input.trim() ? '#475569' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Send size={16} />
              </button>
            </div>
            
            {/* Educational disclaimer */}
            <p style={{ 
              margin: '0.5rem 0 0 0', 
              fontSize: '0.6rem', 
              color: '#64748b', 
              textAlign: 'center' 
            }}>
              Educational purposes only • Not financial advice
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default PageAwareAI;